import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  MessageSquare, 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  Lightbulb,
  Trash2,
  RefreshCw,
  Filter,
  User,
  Mail,
  Calendar,
  Users,
  Crown,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  FileText,
  ChevronDown,
  ChevronUp,
  Settings,
  CreditCard,
  QrCode,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Search,
  Activity,
  Bell,
  Send,
  AlertTriangle,
  Target,
  Zap,
  BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/api';
import AdminBlogManager from '@/components/AdminBlogManager';

const AdminSuggestions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [suggestions, setSuggestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [securityStats, setSecurityStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(7);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [auditFilter, setAuditFilter] = useState('all');
  const [makingAdmin, setMakingAdmin] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [newBlockIp, setNewBlockIp] = useState('');
  const [blockReason, setBlockReason] = useState('');
  
  // New states for search and notifications
  const [userSearch, setUserSearch] = useState('');
  const [pushSubscribers, setPushSubscribers] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState('Evite Dívidas');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationUrl, setNotificationUrl] = useState('/expenses');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]); // Multiple selection
  const [sendingNotification, setSendingNotification] = useState(false);
  const [reminderType, setReminderType] = useState('custom');
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [checkingExpired, setCheckingExpired] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [notificationDiagnostic, setNotificationDiagnostic] = useState(null);
  const [pushDiagnostic, setPushDiagnostic] = useState(null);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/check`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          await fetchAllData();
        }
      } else {
        console.error('Admin check failed:', response.status);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      await Promise.all([fetchStats(), fetchSuggestions(), fetchUsers(), fetchSecurityStats(), fetchAuditLogs(), fetchBlockedIps(), fetchAnalytics(), fetchPushSubscribers(), fetchNotificationDiagnostic(), fetchPushDiagnostic()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchPushSubscribers = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const searchParam = subscriberSearch ? `?search=${encodeURIComponent(subscriberSearch)}` : '';
      const response = await fetch(`${BACKEND_URL}/api/admin/push/subscribers${searchParam}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPushSubscribers(data.subscribers || []);
      }
    } catch (error) {
      console.error('Error fetching push subscribers:', error);
    }
  };

  const fetchNotificationDiagnostic = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/scheduled-tasks-status`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationDiagnostic(data);
      }
    } catch (error) {
      console.error('Error fetching notification diagnostic:', error);
    }
  };

  const fetchPushDiagnostic = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/push/diagnostic`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPushDiagnostic(data);
      }
    } catch (error) {
      console.error('Error fetching push diagnostic:', error);
    }
  };

  const sendNotificationToUser = async (targetUserId) => {
    if (!notificationMessage.trim()) {
      toast.error('Digite uma mensagem para enviar');
      return;
    }
    
    setSendingNotification(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/push/send-to-user/${targetUserId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: notificationTitle,
          message: notificationMessage,
          url: notificationUrl
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Notificação enviada!');
        setNotificationMessage('');
        setSelectedUserId('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar notificação');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Erro ao enviar notificação');
    } finally {
      setSendingNotification(false);
    }
  };

  const sendNotificationToAll = async () => {
    if (!notificationMessage.trim()) {
      toast.error('Digite uma mensagem para enviar');
      return;
    }
    
    if (!window.confirm(`Enviar notificação para ${pushSubscribers.length} usuários?`)) return;
    
    setSendingNotification(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/push/send-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: notificationTitle,
          message: notificationMessage,
          url: notificationUrl
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Notificação enviada! ${data.sent} sucesso, ${data.failed} falha`);
        setNotificationMessage('');
      } else {
        toast.error('Erro ao enviar notificações');
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    } finally {
      setSendingNotification(false);
    }
  };

  const sendNotificationToSelected = async () => {
    if (!notificationMessage.trim()) {
      toast.error('Digite uma mensagem para enviar');
      return;
    }
    
    if (selectedUserIds.length === 0) {
      toast.error('Selecione pelo menos um usuário');
      return;
    }
    
    if (!window.confirm(`Enviar notificação para ${selectedUserIds.length} usuário(s)?`)) return;
    
    setSendingNotification(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      for (const userId of selectedUserIds) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/push/send-to-user/${userId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              title: notificationTitle,
              message: notificationMessage,
              url: notificationUrl
            })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      
      toast.success(`Notificação enviada! ${successCount} sucesso, ${failCount} falha`);
      setNotificationMessage('');
      setSelectedUserIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    } finally {
      setSendingNotification(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(pushSubscribers.map(s => s.user_id));
    }
    setSelectAll(!selectAll);
  };

  const sendReminder = async (type) => {
    // Se tem usuários selecionados, usa eles. Senão, envia para todos
    const targetUsers = selectedUserIds.length > 0 ? selectedUserIds : 'all';
    const targetCount = selectedUserIds.length > 0 ? selectedUserIds.length : pushSubscribers.length;
    
    if (targetCount === 0) {
      toast.error('Nenhum usuário com notificações ativadas');
      return;
    }
    
    const targetLabel = selectedUserIds.length > 0 
      ? `${selectedUserIds.length} usuário(s) selecionado(s)` 
      : `todos os ${pushSubscribers.length} usuários`;
    
    const typeLabels = {
      'trial_expiring': 'Teste Acabando',
      'plan_expiring': 'Plano Expirando',
      'inactivity': 'Inatividade',
      'analysis_reminder': 'Lembrete de Análise',
      'custom': 'Personalizado'
    };
    
    if (!window.confirm(`Enviar lembrete "${typeLabels[type] || type}" para ${targetLabel}?`)) return;
    
    setSendingNotification(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      console.log('Sending reminder with params:', {
        reminder_type: type,
        target_users: targetUsers,
        selectedUserIds: selectedUserIds
      });
      
      const response = await fetch(`${BACKEND_URL}/api/admin/push/send-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reminder_type: type,
          target_users: targetUsers,
          custom_message: type === 'custom' ? notificationMessage : '',
          days: 3
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Mostrar feedback detalhado
        if (data.sent > 0 && data.failed === 0) {
          toast.success(`✅ Lembrete enviado com sucesso para ${data.sent} usuário(s)!`);
        } else if (data.sent > 0 && data.failed > 0) {
          toast.success(`✅ Enviado para ${data.sent} usuário(s)`);
          toast.error(`❌ Falha para ${data.failed}: ${data.failed_users?.join(', ') || 'desconhecido'}`);
        } else if (data.sent === 0 && data.failed > 0) {
          toast.error(`❌ Falha total! Nenhuma notificação enviada. Falhas: ${data.failed_users?.join(', ') || 'desconhecido'}`);
        } else if (data.total === 0) {
          toast.warning('⚠️ Nenhum usuário encontrado para enviar');
        }
        
        // Limpar seleção após envio bem-sucedido
        if (selectedUserIds.length > 0 && data.sent > 0) {
          setSelectedUserIds([]);
          setSelectAll(false);
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao enviar lembrete');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Erro de conexão ao enviar lembrete');
    } finally {
      setSendingNotification(false);
    }
  };

  const checkExpiredSubscriptions = async () => {
    setCheckingExpired(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/check-expired-subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.downgraded_count > 0) {
          toast.success(`${data.downgraded_count} plano(s) expirado(s) atualizado(s) para Free!`);
          fetchUsers(); // Refresh user list
        } else {
          toast.info('Nenhum plano expirado encontrado');
        }
      } else {
        toast.error('Erro ao verificar planos');
      }
    } catch (error) {
      console.error('Error checking expired subscriptions:', error);
      toast.error('Erro ao verificar planos');
    } finally {
      setCheckingExpired(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics?days=${analyticsPeriod}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        console.error('Analytics response not ok:', response.status);
        // Set empty data structure so UI doesn't break
        setAnalyticsData({
          summary: { total_page_views: 0, total_searches: 0, all_time_views: 0, all_time_searches: 0, anonymous_views: 0, logged_views: 0 },
          top_pages: [],
          top_searches: [],
          recent_searches: [],
          daily_views: [],
          users_by_page: [],
          recent_activity: [],
          recent_anonymous_activity: [],
          anonymous_sessions: [],
          traffic_sources: [],
          referrers: []
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Set empty data structure on error
      setAnalyticsData({
        summary: { total_page_views: 0, total_searches: 0, all_time_views: 0, all_time_searches: 0, anonymous_views: 0, logged_views: 0 },
        top_pages: [],
        top_searches: [],
        recent_searches: [],
        daily_views: [],
        users_by_page: [],
        recent_activity: [],
        recent_anonymous_activity: [],
        anonymous_sessions: [],
        traffic_sources: [],
        referrers: []
      });
    }
  };

  const fetchSecurityStats = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/security-stats`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSecurityStats(data);
      }
    } catch (error) {
      console.error('Error fetching security stats:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const url = auditFilter === 'all' 
        ? `${BACKEND_URL}/api/admin/audit-logs?limit=50`
        : `${BACKEND_URL}/api/admin/audit-logs?severity=${auditFilter}&limit=50`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchBlockedIps = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/blocked-ips`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlockedIps(data.blocked_ips || []);
      }
    } catch (error) {
      console.error('Error fetching blocked IPs:', error);
    }
  };

  const blockIp = async () => {
    if (!newBlockIp.trim()) {
      toast.error('Digite um IP para bloquear');
      return;
    }
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/block-ip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          ip_address: newBlockIp.trim(),
          reason: blockReason || 'Bloqueado manualmente'
        })
      });
      
      if (response.ok) {
        toast.success(`IP ${newBlockIp} bloqueado!`);
        setNewBlockIp('');
        setBlockReason('');
        fetchBlockedIps();
        fetchSecurityStats();
      } else {
        toast.error('Erro ao bloquear IP');
      }
    } catch (error) {
      console.error('Error blocking IP:', error);
      toast.error('Erro ao bloquear IP');
    }
  };

  const unblockIp = async (ip) => {
    if (!window.confirm(`Desbloquear IP ${ip}?`)) return;
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/unblock-ip/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        toast.success(`IP ${ip} desbloqueado!`);
        fetchBlockedIps();
        fetchSecurityStats();
      } else {
        toast.error('Erro ao desbloquear IP');
      }
    } catch (error) {
      console.error('Error unblocking IP:', error);
      toast.error('Erro ao desbloquear IP');
    }
  };

  const fetchStats = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Set default stats if fetch fails
        setStats({
          users: { total: 0, by_plan: { free: 0, basic: 0, premium: 0 }, recent_signups: 0 },
          analyses: { total: 0 },
          suggestions: { total: 0, pending: 0 },
          revenue: { active_basic: 0, active_premium: 0, monthly_estimate: 0 }
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        users: { total: 0, by_plan: { free: 0, basic: 0, premium: 0 }, recent_signups: 0 },
        analyses: { total: 0 },
        suggestions: { total: 0, pending: 0 },
        revenue: { active_basic: 0, active_premium: 0, monthly_estimate: 0 }
      });
    }
  };

  const fetchSuggestions = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/suggestions`
        : `${BACKEND_URL}/api/suggestions?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (planFilter !== 'all') params.append('plan', planFilter);
      if (userSearch) params.append('search', userSearch);
      const queryString = params.toString();
      const url = `${BACKEND_URL}/api/admin/users${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const makeCurrentUserAdmin = async () => {
    setMakingAdmin(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/make-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        toast.success('Você agora é administrador!');
        setIsAdmin(true);
        fetchAllData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao tornar-se admin');
      }
    } catch (error) {
      console.error('Error making admin:', error);
      toast.error('Erro ao tornar-se admin');
    } finally {
      setMakingAdmin(false);
    }
  };

  const updateSuggestionStatus = async (suggestionId, newStatus) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/suggestions/${suggestionId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status atualizado para: ${getStatusLabel(newStatus)}`);
        fetchSuggestions();
        fetchStats();
      } else {
        toast.error('Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteSuggestion = async (suggestionId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta sugestão?')) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/suggestions/${suggestionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (response.ok) {
        toast.success('Sugestão deletada');
        fetchSuggestions();
        fetchStats();
      } else {
        toast.error('Erro ao deletar sugestão');
      }
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      toast.error('Erro ao deletar sugestão');
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/plan`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan: newPlan })
      });

      if (response.ok) {
        toast.success(`Plano atualizado para: ${getPlanLabel(newPlan)}`);
        fetchUsers();
        fetchStats();
      } else {
        toast.error('Erro ao atualizar plano');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Erro ao atualizar plano');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'reviewed': return 'text-blue-500 bg-blue-500/10';
      case 'implemented': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'reviewed': return 'Revisado';
      case 'implemented': return 'Implementado';
      default: return status;
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'free': return 'text-gray-400 bg-gray-500/10';
      case 'basic': return 'text-blue-500 bg-blue-500/10';
      case 'premium': return 'text-[#00FF94] bg-[#00FF94]/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getPlanLabel = (plan) => {
    switch (plan) {
      case 'free': return 'Gratuito';
      case 'basic': return 'Básico';
      case 'premium': return 'Premium';
      default: return plan;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Acesso Restrito</h1>
          <p className="text-gray-400 mb-6">Você precisa estar logado para acessar esta página.</p>
          <Button onClick={() => navigate('/')} className="bg-[#00FF94] text-black hover:bg-[#00CC76]">
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <Shield className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Área Administrativa</h1>
          <p className="text-gray-400 mb-6">
            Esta área é restrita a administradores. Se você é o proprietário do sistema, 
            clique no botão abaixo para se tornar administrador.
          </p>
          <div className="space-y-4">
            <Button 
              onClick={makeCurrentUserAdmin}
              disabled={makingAdmin}
              className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76]"
            >
              {makingAdmin ? 'Processando...' : 'Tornar-me Administrador'}
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="w-full text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-[#00FF94]" />
                <span className="font-bold text-xl text-white">Painel Administrativo</span>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={fetchAllData}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Tab Navigation */}
      <div className="pt-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'analytics', label: 'Monitoramento', icon: Activity },
              { id: 'notifications', label: 'Notificações', icon: Bell },
              { id: 'blog', label: 'Blog', icon: BookOpen },
              { id: 'users', label: 'Usuários', icon: Users },
              { id: 'suggestions', label: 'Sugestões', icon: MessageSquare },
              { id: 'security', label: 'Segurança', icon: Shield }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'text-[#00FF94] border-[#00FF94]'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-8 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {!stats ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
            <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-blue-500" />
                  <span className="text-xs text-gray-500">Total</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats.users.total}</div>
                <div className="text-gray-400 text-sm">Usuários cadastrados</div>
                <div className="text-xs text-green-500 mt-2">
                  +{stats.users.recent_signups} últimos 7 dias
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <div className="flex items-center justify-between mb-4">
                  <FileText className="w-8 h-8 text-purple-500" />
                  <span className="text-xs text-gray-500">Total</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats.analyses.total}</div>
                <div className="text-gray-400 text-sm">Análises realizadas</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <div className="flex items-center justify-between mb-4">
                  <MessageSquare className="w-8 h-8 text-yellow-500" />
                  <span className="text-xs text-gray-500">Pendentes</span>
                </div>
                <div className="text-3xl font-bold text-white">{stats.suggestions.pending}</div>
                <div className="text-gray-400 text-sm">de {stats.suggestions.total} sugestões</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-[#00FF94]" />
                  <div className="flex items-center gap-1">
                    {stats.revenue.growth_percentage >= 0 ? (
                      <ArrowUpRight className="w-4 h-4 text-green-500" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-xs ${stats.revenue.growth_percentage >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {stats.revenue.growth_percentage > 0 ? '+' : ''}{stats.revenue.growth_percentage || 0}%
                    </span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">
                  R$ {(stats.revenue.total_received?.total || 0).toFixed(2).replace('.', ',')}
                </div>
                <div className="text-gray-400 text-sm">Receita total</div>
              </motion.div>
            </div>

            {/* Revenue Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Receita por Método de Pagamento</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#141414] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-400">Stripe (Cartão/Boleto)</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    R$ {(stats.revenue.total_received?.stripe || 0).toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.revenue.transaction_count?.stripe || 0} transações
                  </div>
                </div>

                <div className="bg-[#141414] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="w-5 h-5 text-[#00FF94]" />
                    <span className="text-gray-400">PIX (Mercado Pago)</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    R$ {(stats.revenue.total_received?.pix || 0).toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.revenue.transaction_count?.pix || 0} transações
                  </div>
                </div>

                <div className="bg-[#141414] rounded-lg p-4 border border-[#00FF94]/20">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-5 h-5 text-[#00FF94]" />
                    <span className="text-[#00FF94]">Total Geral</span>
                  </div>
                  <div className="text-2xl font-bold text-[#00FF94]">
                    R$ {(stats.revenue.total_received?.total || 0).toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.revenue.transaction_count?.total || 0} transações
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Monthly Comparison Chart */}
            {stats.monthly_comparison && stats.monthly_comparison.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Comparativo Mensal (Últimos 6 meses)</h3>
                
                {/* Chart */}
                <div className="mb-6">
                  <div className="flex items-end gap-2 h-48">
                    {stats.monthly_comparison.map((month, index) => {
                      const maxRevenue = Math.max(...stats.monthly_comparison.map(m => m.revenue.total), 1);
                      const heightPercentage = (month.revenue.total / maxRevenue) * 100;
                      const stripePercentage = month.revenue.total > 0 
                        ? (month.revenue.stripe / month.revenue.total) * 100 
                        : 0;
                      
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center gap-2">
                          <div 
                            className="w-full relative rounded-t-lg overflow-hidden bg-gray-800 transition-all hover:opacity-80"
                            style={{ height: `${Math.max(heightPercentage, 5)}%` }}
                          >
                            {/* Stripe portion */}
                            <div 
                              className="absolute bottom-0 w-full bg-blue-500"
                              style={{ height: `${stripePercentage}%` }}
                            />
                            {/* PIX portion */}
                            <div 
                              className="absolute top-0 w-full bg-[#00FF94]"
                              style={{ height: `${100 - stripePercentage}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-400 text-center">
                            {month.month_label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-blue-500" />
                      <span className="text-xs text-gray-400">Stripe</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-[#00FF94]" />
                      <span className="text-xs text-gray-400">PIX</span>
                    </div>
                  </div>
                </div>

                {/* Monthly Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 text-gray-400 font-medium">Mês</th>
                        <th className="text-right py-2 text-gray-400 font-medium">Stripe</th>
                        <th className="text-right py-2 text-gray-400 font-medium">PIX</th>
                        <th className="text-right py-2 text-gray-400 font-medium">Total</th>
                        <th className="text-right py-2 text-gray-400 font-medium">Transações</th>
                        <th className="text-right py-2 text-gray-400 font-medium">Usuários</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.monthly_comparison.map((month, index) => (
                        <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 text-white">{month.month_label}</td>
                          <td className="py-3 text-right text-blue-400">
                            R$ {month.revenue.stripe.toFixed(2).replace('.', ',')}
                          </td>
                          <td className="py-3 text-right text-[#00FF94]">
                            R$ {month.revenue.pix.toFixed(2).replace('.', ',')}
                          </td>
                          <td className="py-3 text-right text-white font-semibold">
                            R$ {month.revenue.total.toFixed(2).replace('.', ',')}
                          </td>
                          <td className="py-3 text-right text-gray-400">
                            {month.transactions.total}
                          </td>
                          <td className="py-3 text-right text-gray-400">
                            +{month.new_users}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Recent Transactions */}
            {stats.recent_transactions && stats.recent_transactions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Últimas Transações</h3>
                <div className="space-y-3">
                  {stats.recent_transactions.map((txn, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#141414] rounded-lg">
                      <div className="flex items-center gap-3">
                        {txn.type === 'pix' ? (
                          <div className="w-10 h-10 rounded-full bg-[#00FF94]/10 flex items-center justify-center">
                            <QrCode className="w-5 h-5 text-[#00FF94]" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-blue-500" />
                          </div>
                        )}
                        <div>
                          <div className="text-white font-medium">{txn.package}</div>
                          <div className="text-xs text-gray-400">{txn.method}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-semibold ${txn.type === 'pix' ? 'text-[#00FF94]' : 'text-blue-400'}`}>
                          R$ {(txn.amount || 0).toFixed(2).replace('.', ',')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {txn.date ? new Date(txn.date).toLocaleDateString('pt-BR') : '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Users by Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Usuários por Plano</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#141414] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-400">Gratuito</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.users.by_plan.free}</div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-gray-400 h-2 rounded-full" 
                      style={{ width: `${stats.users.total > 0 ? (stats.users.by_plan.free / stats.users.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                <div className="bg-[#141414] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-blue-500" />
                    <span className="text-blue-500">Básico</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.users.by_plan.basic}</div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${stats.users.total > 0 ? (stats.users.by_plan.basic / stats.users.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                <div className="bg-[#141414] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-[#00FF94]" />
                    <span className="text-[#00FF94]">Premium</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.users.by_plan.premium}</div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-[#00FF94] h-2 rounded-full" 
                      style={{ width: `${stats.users.total > 0 ? (stats.users.by_plan.premium / stats.users.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Active Subscriptions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Assinaturas Ativas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between bg-[#141414] rounded-lg p-4">
                  <div>
                    <div className="text-gray-400 text-sm">Plano Básico</div>
                    <div className="text-xl font-bold text-white">{stats.revenue.active_basic} assinantes</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 text-sm">R$ 9,90/mês</div>
                    <div className="text-blue-500 font-semibold">
                      R$ {(stats.revenue.active_basic * 9.90).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-[#141414] rounded-lg p-4">
                  <div>
                    <div className="text-gray-400 text-sm">Plano Premium</div>
                    <div className="text-xl font-bold text-white">{stats.revenue.active_premium} assinantes</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 text-sm">R$ 29,90/mês</div>
                    <div className="text-[#00FF94] font-semibold">
                      R$ {(stats.revenue.active_premium * 29.90).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
          )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Period Filter */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-xl font-semibold text-white">Monitoramento de Atividade</h2>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Período:</span>
                {[7, 14, 30].map((days) => (
                  <Button
                    key={days}
                    variant={analyticsPeriod === days ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      setAnalyticsPeriod(days);
                      setTimeout(fetchAnalytics, 100);
                    }}
                    className={analyticsPeriod === days 
                      ? 'bg-[#00FF94] text-black' 
                      : 'text-gray-400 hover:text-white'
                    }
                  >
                    {days} dias
                  </Button>
                ))}
              </div>
            </div>

            {!analyticsData ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#00FF94] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Eye className="w-8 h-8 text-blue-500" />
                      <span className="text-xs text-gray-500">Período</span>
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.summary.total_page_views}</div>
                    <div className="text-gray-400 text-sm">Visualizações</div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Search className="w-8 h-8 text-purple-500" />
                      <span className="text-xs text-gray-500">Período</span>
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.summary.total_searches}</div>
                    <div className="text-gray-400 text-sm">Pesquisas</div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Eye className="w-8 h-8 text-[#00FF94]" />
                      <span className="text-xs text-gray-500">Total</span>
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.summary.all_time_views}</div>
                    <div className="text-gray-400 text-sm">Views (todos os tempos)</div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Search className="w-8 h-8 text-yellow-500" />
                      <span className="text-xs text-gray-500">Total</span>
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.summary.all_time_searches}</div>
                    <div className="text-gray-400 text-sm">Pesquisas (todos os tempos)</div>
                  </motion.div>
                </div>

                {/* Daily Views Chart */}
                {analyticsData.daily_views && analyticsData.daily_views.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <h3 className="text-lg font-semibold text-white mb-4">Visualizações por Dia</h3>
                    <div className="flex items-end gap-1 h-40">
                      {analyticsData.daily_views.map((day, index) => {
                        const maxViews = Math.max(...analyticsData.daily_views.map(d => d.views), 1);
                        const heightPercentage = (day.views / maxViews) * 100;
                        
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center gap-1">
                            <div className="text-xs text-gray-400">{day.views}</div>
                            <div 
                              className="w-full bg-[#00FF94] rounded-t-sm transition-all hover:bg-[#00FF94]/80"
                              style={{ height: `${Math.max(heightPercentage, 5)}%` }}
                            />
                            <div className="text-xs text-gray-500 truncate w-full text-center">
                              {day.date?.slice(5) || ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Top Pages and Top Searches */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Pages */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-blue-500" />
                      Páginas Mais Visitadas
                    </h3>
                    {analyticsData.top_pages.length === 0 ? (
                      <p className="text-gray-400 text-center py-8">Nenhum dado disponível</p>
                    ) : (
                      <div className="space-y-3">
                        {analyticsData.top_pages.map((page, index) => {
                          const maxViews = analyticsData.top_pages[0]?.views || 1;
                          const percentage = (page.views / maxViews) * 100;
                          
                          return (
                            <div key={index} className="relative">
                              <div 
                                className="absolute inset-0 bg-blue-500/10 rounded-lg"
                                style={{ width: `${percentage}%` }}
                              />
                              <div className="relative flex items-center justify-between p-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-500 text-sm w-6">{index + 1}.</span>
                                  <div>
                                    <span className="text-white text-sm">{page.page_translated || page.page}</span>
                                    <span className="text-gray-500 text-xs ml-2 font-mono">({page.page})</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-gray-400">{page.unique_visitors} visitantes</span>
                                  <span className="text-white font-semibold">{page.views} views</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>

                  {/* Traffic Sources */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.42 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                    data-testid="traffic-sources-section"
                  >
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#00FF94]" />
                      Origem do Tráfego (Redes Sociais)
                    </h3>
                    <p className="text-gray-500 text-xs mb-4">
                      Use links com ?utm_source=instagram (ou facebook, tiktok, etc) para rastrear
                    </p>
                    {!analyticsData.traffic_sources || analyticsData.traffic_sources.length === 0 ? (
                      <div className="text-center py-8">
                        <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                        <p className="text-gray-400">Nenhuma fonte de tráfego rastreada</p>
                        <p className="text-gray-500 text-xs mt-2">
                          Dica: Compartilhe links como:<br/>
                          <code className="text-[#00FF94]">seusite.com?utm_source=instagram</code>
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {analyticsData.traffic_sources.map((source, index) => {
                          const maxVisits = analyticsData.traffic_sources[0]?.visits || 1;
                          const percentage = (source.visits / maxVisits) * 100;
                          
                          // Icon based on source
                          const getSourceIcon = (sourceName) => {
                            const s = sourceName?.toLowerCase();
                            if (s?.includes('instagram')) return '📸';
                            if (s?.includes('facebook')) return '📘';
                            if (s?.includes('tiktok')) return '🎵';
                            if (s?.includes('twitter') || s?.includes('x')) return '🐦';
                            if (s?.includes('youtube')) return '▶️';
                            if (s?.includes('linkedin')) return '💼';
                            if (s?.includes('whatsapp')) return '💬';
                            if (s?.includes('google')) return '🔍';
                            if (s?.includes('email')) return '📧';
                            return '🌐';
                          };
                          
                          return (
                            <div key={index} className="relative">
                              <div 
                                className="absolute inset-0 bg-[#00FF94]/10 rounded-lg"
                                style={{ width: `${percentage}%` }}
                              />
                              <div className="relative flex items-center justify-between p-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{getSourceIcon(source.source)}</span>
                                  <span className="text-white font-semibold capitalize">{source.source}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-gray-400">{source.unique_visitors} únicos</span>
                                  <span className="text-[#00FF94] font-semibold">{source.visits} visitas</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>

                  {/* Top Searches */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Search className="w-5 h-5 text-purple-500" />
                      Pesquisas Mais Comuns
                    </h3>
                    {analyticsData.top_searches.length === 0 ? (
                      <p className="text-gray-400 text-center py-8">Nenhuma pesquisa registrada</p>
                    ) : (
                      <div className="space-y-3">
                        {analyticsData.top_searches.map((search, index) => {
                          const maxCount = analyticsData.top_searches[0]?.count || 1;
                          const percentage = (search.count / maxCount) * 100;
                          
                          return (
                            <div key={index} className="relative">
                              <div 
                                className="absolute inset-0 bg-purple-500/10 rounded-lg"
                                style={{ width: `${percentage}%` }}
                              />
                              <div className="relative flex items-center justify-between p-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-500 text-sm w-6">{index + 1}.</span>
                                  <span className="text-white">{search.query}</span>
                                </div>
                                <span className="text-purple-400 font-semibold">{search.count}x</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                </div>

                {/* Recent Searches */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                >
                  <h3 className="text-lg font-semibold text-white mb-4">Pesquisas Recentes</h3>
                  {analyticsData.recent_searches.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">Nenhuma pesquisa registrada</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-gray-400 font-medium">Pesquisa</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Página</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Data</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.recent_searches.slice(0, 20).map((search, index) => (
                            <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 text-white">{search.query}</td>
                              <td className="py-3 text-gray-400 font-mono text-xs">{search.path || '-'}</td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(search.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>

                {/* Users by Page - NEW SECTION */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  data-testid="users-by-page-section"
                >
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#00FF94]" />
                    Usuários por Página
                  </h3>
                  {!analyticsData.users_by_page || analyticsData.users_by_page.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">Nenhum dado de usuário disponível</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-gray-400 font-medium">Email</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Nome</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Plano</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Página</th>
                            <th className="text-center py-2 text-gray-400 font-medium">Visitas</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Última Visita</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.users_by_page.slice(0, 30).map((item, index) => (
                            <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 text-white">{item.email}</td>
                              <td className="py-3 text-gray-400">{item.name || '-'}</td>
                              <td className="py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${getPlanColor(item.plan)}`}>
                                  {getPlanLabel(item.plan)}
                                </span>
                              </td>
                              <td className="py-3">
                                <span className="text-white text-xs">{item.page_translated || item.page}</span>
                              </td>
                              <td className="py-3 text-center text-[#00FF94] font-semibold">{item.visits}</td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(item.last_visit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>

                {/* Recent Activity - NEW SECTION */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  data-testid="recent-activity-section"
                >
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-yellow-500" />
                    Atividade Recente
                  </h3>
                  {!analyticsData.recent_activity || analyticsData.recent_activity.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">Nenhuma atividade recente registrada</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-gray-400 font-medium">Email</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Nome</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Página Visitada</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Data/Hora</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.recent_activity.slice(0, 30).map((activity, index) => (
                            <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 text-white">{activity.email}</td>
                              <td className="py-3 text-gray-400">{activity.name || '-'}</td>
                              <td className="py-3">
                                <span className="text-white text-xs">{activity.page_translated || activity.page}</span>
                              </td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(activity.timestamp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>

                {/* Anonymous Visitors Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  data-testid="anonymous-activity-section"
                >
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-500" />
                    Visitantes Anônimos
                    {analyticsData.summary?.anonymous_views > 0 && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full ml-2">
                        {analyticsData.summary.anonymous_views} visitas
                      </span>
                    )}
                  </h3>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                      <p className="text-purple-400 text-sm">Visitas Anônimas</p>
                      <p className="text-2xl font-bold text-white">{analyticsData.summary?.anonymous_views || 0}</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                      <p className="text-green-400 text-sm">Visitas Logadas</p>
                      <p className="text-2xl font-bold text-white">{analyticsData.summary?.logged_views || 0}</p>
                    </div>
                  </div>
                  
                  {!analyticsData.recent_anonymous_activity || analyticsData.recent_anonymous_activity.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">Nenhuma atividade anônima recente</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-gray-400 font-medium">Sessão</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Página</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Dispositivo</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Origem</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Data/Hora</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.recent_anonymous_activity.slice(0, 30).map((activity, index) => (
                            <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3">
                                <span className="text-purple-400 text-xs font-mono">
                                  {activity.anonymous_session_id?.slice(0, 15) || 'N/A'}...
                                </span>
                              </td>
                              <td className="py-3">
                                <span className="text-white text-xs">{activity.page_translated || activity.page}</span>
                              </td>
                              <td className="py-3 text-gray-400 text-xs">
                                {activity.device_info?.device || 'Desconhecido'}
                              </td>
                              <td className="py-3 text-gray-400 text-xs">
                                {activity.utm_source || activity.referrer?.slice(0, 20) || 'Direto'}
                              </td>
                              <td className="py-3 text-gray-500 text-xs">{formatDate(activity.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6" data-testid="notifications-tab">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Bell className="w-6 h-6 text-[#00FF94]" />
              Enviar Notificações Push
            </h2>

            {/* Automatic Notifications Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-[#00FF94]/10 to-[#0A0A0A] rounded-xl p-6 border border-[#00FF94]/20"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#00FF94]" />
                    Notificações Automáticas
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Sistema ativo • Verificações às 9h e 15h (horário de Brasília)
                  </p>
                  <ul className="text-xs text-gray-500 mt-2 space-y-1">
                    <li>• Trial expirando (3 dias antes)</li>
                    <li>• Plano expirando (5 dias antes)</li>
                    <li>• Inatividade (7+ dias sem registrar despesas)</li>
                  </ul>
                </div>
                <Button
                  onClick={async () => {
                    setSendingNotification(true);
                    try {
                      const sessionToken = localStorage.getItem('session_token');
                      const response = await fetch(`${BACKEND_URL}/api/admin/run-auto-notifications`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${sessionToken}` }
                      });
                      if (response.ok) {
                        const data = await response.json();
                        toast.success(data.message);
                      } else {
                        toast.error('Erro ao executar notificações');
                      }
                    } catch (error) {
                      toast.error('Erro ao executar notificações');
                    } finally {
                      setSendingNotification(false);
                    }
                  }}
                  disabled={sendingNotification}
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76]"
                >
                  {sendingNotification ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Executar Agora
                </Button>
              </div>

              {/* Diagnostic Info */}
              {notificationDiagnostic && (
                <div className="mt-4 p-4 bg-[#141414] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white">Diagnóstico</h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={fetchNotificationDiagnostic}
                      className="text-gray-400 hover:text-white"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Push Subscriptions:</span>
                      <span className="text-white ml-2 font-semibold">{notificationDiagnostic.total_push_subscriptions || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Scheduler:</span>
                      <span className={`ml-2 font-semibold ${notificationDiagnostic.scheduler_running ? 'text-[#00FF94]' : 'text-red-500'}`}>
                        {notificationDiagnostic.scheduler_running ? 'Ativo' : 'Parado'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Users needing notification */}
                  {notificationDiagnostic.users_needing_notification?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <h5 className="text-xs text-gray-400 mb-2">Usuários com trial expirando/expirado:</h5>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {notificationDiagnostic.users_needing_notification.map((u, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-white">{u.email}</span>
                            <div className="flex items-center gap-2">
                              <span className={u.days_until_expiry < 0 ? 'text-red-400' : 'text-yellow-400'}>
                                {u.status}
                              </span>
                              <span className={u.has_push_subscription ? 'text-[#00FF94]' : 'text-red-400'}>
                                {u.has_push_subscription ? '🔔' : '🔕'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        🔔 = Push ativo | 🔕 = Push não ativado (não receberá notificação)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Quick Reminder Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Lembretes Manuais Rápidos</h3>
              <p className="text-gray-400 text-sm mb-4">
                {selectedUserIds.length > 0 
                  ? `📍 ${selectedUserIds.length} usuário(s) selecionado(s) - o lembrete será enviado apenas para eles.`
                  : 'Selecione usuários acima ou clique para enviar para todos com notificações ativadas.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button
                  onClick={() => sendReminder('trial_expiring')}
                  disabled={sendingNotification}
                  className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 hover:bg-yellow-500/20"
                >
                  <Clock className="w-4 h-4" />
                  Trial Expirando
                </Button>
                <Button
                  onClick={() => sendReminder('plan_expiring')}
                  disabled={sendingNotification}
                  className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-500 hover:bg-orange-500/20"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Plano Expirando
                </Button>
                <Button
                  onClick={() => sendReminder('inactivity')}
                  disabled={sendingNotification}
                  className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-500 hover:bg-blue-500/20"
                >
                  <Users className="w-4 h-4" />
                  Inatividade
                </Button>
                <Button
                  onClick={() => sendReminder('analysis_reminder')}
                  disabled={sendingNotification}
                  className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 text-purple-500 hover:bg-purple-500/20"
                >
                  <Target className="w-4 h-4" />
                  Fazer Análise
                </Button>
              </div>
            </motion.div>

            {/* Custom Notification Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Notificação Personalizada</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Título</label>
                  <input
                    type="text"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    className="w-full px-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]"
                    placeholder="Evite Dívidas"
                    data-testid="notification-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Mensagem *</label>
                  <textarea
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94] resize-none"
                    placeholder="Digite sua mensagem aqui..."
                    data-testid="notification-message-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">URL de destino</label>
                  <select
                    value={notificationUrl}
                    onChange={(e) => setNotificationUrl(e.target.value)}
                    className="w-full px-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00FF94]"
                  >
                    <option value="/expenses">Organizador de Despesas</option>
                    <option value="/analysis">Nova Análise</option>
                    <option value="/pricing">Planos e Preços</option>
                    <option value="/investments">Investimentos</option>
                    <option value="/">Página Inicial</option>
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    onClick={sendNotificationToSelected}
                    disabled={sendingNotification || !notificationMessage.trim() || selectedUserIds.length === 0}
                    className="flex-1 bg-[#00FF94] text-black hover:bg-[#00CC76]"
                    data-testid="send-to-selected-btn"
                  >
                    {sendingNotification ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Enviar para Selecionados ({selectedUserIds.length})
                  </Button>
                  <Button
                    onClick={sendNotificationToAll}
                    disabled={sendingNotification || !notificationMessage.trim() || pushSubscribers.length === 0}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="send-to-all-btn"
                  >
                    Enviar para Todos ({pushSubscribers.length})
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Select Users to Send */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Selecionar Destinatários
                </h3>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400">
                    {selectedUserIds.length} de {pushSubscribers.length} selecionado(s)
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleSelectAll}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {selectAll ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </Button>
                </div>
              </div>
              
              {/* Search subscribers */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuário por nome ou email..."
                  value={subscriberSearch}
                  onChange={(e) => {
                    setSubscriberSearch(e.target.value);
                    setTimeout(fetchPushSubscribers, 300);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]"
                  data-testid="subscriber-search-input"
                />
              </div>

              {/* Subscribers list with checkboxes */}
              <div className="max-h-96 overflow-y-auto space-y-2">
                {pushSubscribers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum usuário com notificações ativadas</p>
                    <p className="text-xs mt-2">Os usuários precisam permitir notificações no navegador</p>
                  </div>
                ) : (
                  pushSubscribers.map((subscriber) => (
                    <div
                      key={subscriber.user_id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedUserIds.includes(subscriber.user_id)
                          ? 'bg-[#00FF94]/10 border-[#00FF94]'
                          : 'bg-[#141414] border-white/5 hover:border-white/20'
                      }`}
                      onClick={() => toggleUserSelection(subscriber.user_id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(subscriber.user_id)}
                          onChange={() => toggleUserSelection(subscriber.user_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-gray-600 text-[#00FF94] focus:ring-[#00FF94] bg-[#141414]"
                        />
                        {subscriber.picture ? (
                          <img src={subscriber.picture} alt={subscriber.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="text-white text-sm">{subscriber.name}</div>
                          <div className="text-gray-400 text-xs">{subscriber.email}</div>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getPlanColor(subscriber.subscription_type)}`}>
                        {getPlanLabel(subscriber.subscription_type)}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4 p-3 bg-[#141414] rounded-lg border border-white/5">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Zap className="w-4 h-4 text-[#00FF94]" />
                  <span>{pushSubscribers.length} usuário(s) com notificações ativadas</span>
                </div>
              </div>
            </motion.div>

            {/* Push Diagnostic Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-orange-500" />
                  Diagnóstico de Push
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchPushDiagnostic}
                  className="text-gray-400 hover:text-white"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {pushDiagnostic ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-[#141414] p-3 rounded-lg">
                      <span className="text-gray-400 block">Subscriptions</span>
                      <span className="text-white text-xl font-bold">{pushDiagnostic.total_subscriptions}</span>
                    </div>
                    <div className="bg-[#141414] p-3 rounded-lg">
                      <span className="text-gray-400 block">VAPID Config</span>
                      <span className={pushDiagnostic.vapid_configured ? 'text-[#00FF94]' : 'text-red-500'}>
                        {pushDiagnostic.vapid_configured ? '✅ OK' : '❌ Falta'}
                      </span>
                    </div>
                    <div className="bg-[#141414] p-3 rounded-lg">
                      <span className="text-gray-400 block">Private Key</span>
                      <span className={pushDiagnostic.vapid_private_key_exists ? 'text-[#00FF94]' : 'text-red-500'}>
                        {pushDiagnostic.vapid_private_key_exists ? '✅ OK' : '❌ Falta'}
                      </span>
                    </div>
                  </div>

                  {pushDiagnostic.subscriptions?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-gray-400 font-medium">Email</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Navegador</th>
                            <th className="text-left py-2 text-gray-400 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pushDiagnostic.subscriptions.map((sub, index) => (
                            <tr key={index} className="border-b border-white/5">
                              <td className="py-2 text-white">{sub.email}</td>
                              <td className="py-2 text-gray-400">{sub.browser}</td>
                              <td className="py-2">
                                <span className={sub.has_keys ? 'text-[#00FF94]' : 'text-red-400'}>
                                  {sub.has_keys ? '✅ Válido' : '❌ Sem keys'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Carregando diagnóstico...</p>
              )}
            </motion.div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <AdminBlogManager />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Search Bar */}
              <div className="relative flex-1 w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                  className="w-full pl-10 pr-4 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]"
                  data-testid="user-search-input"
                />
                {userSearch && (
                  <button
                    onClick={() => {
                      setUserSearch('');
                      setTimeout(fetchUsers, 100);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    ×
                  </button>
                )}
              </div>
              <Button
                size="sm"
                onClick={fetchUsers}
                className="bg-[#00FF94] text-black hover:bg-[#00CC76]"
              >
                <Search className="w-4 h-4 mr-1" />
                Buscar
              </Button>
            </div>

            {/* Plan Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-5 h-5 text-gray-400" />
              <span className="text-gray-400 text-sm">Filtrar por plano:</span>
              {['all', 'free', 'basic', 'premium'].map((plan) => (
                <Button
                  key={plan}
                  variant={planFilter === plan ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setPlanFilter(plan);
                    setTimeout(fetchUsers, 100);
                  }}
                  className={planFilter === plan 
                    ? 'bg-[#00FF94] text-black' 
                    : 'text-gray-400 hover:text-white'
                  }
                >
                  {plan === 'all' ? 'Todos' : getPlanLabel(plan)}
                </Button>
              ))}
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">
                {users.length} usuário(s) encontrado(s)
                {userSearch && ` para "${userSearch}"`}
              </div>
              <Button
                size="sm"
                onClick={checkExpiredSubscriptions}
                disabled={checkingExpired}
                className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30"
                data-testid="check-expired-btn"
              >
                {checkingExpired ? (
                  <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Verificar Planos Expirados
              </Button>
            </div>

            {/* Users List */}
            <div className="space-y-3">
              {users.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Nenhum usuário encontrado</h3>
                  {userSearch && (
                    <p className="text-gray-400">Tente uma busca diferente</p>
                  )}
                </div>
              ) : (
                users.map((u, index) => (
                  <motion.div
                    key={u.user_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="bg-[#0A0A0A] rounded-xl border border-white/5 overflow-hidden"
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-white/5 transition-all"
                      onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{u.name}</span>
                              {u.is_admin && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                                  Admin
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-400">{u.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm ${getPlanColor(u.subscription_type)}`}>
                              {getPlanLabel(u.subscription_type)}
                            </span>
                            {u.subscription_expired && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-500 animate-pulse">
                                EXPIRADO
                              </span>
                            )}
                          </div>
                          {expandedUser === u.user_id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedUser === u.user_id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 border-t border-white/5"
                      >
                        <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Análises realizadas</div>
                            <div className="text-white font-medium">{u.analyses_count || 0}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Análises este mês</div>
                            <div className="text-white font-medium">{u.analyses_this_month || 0}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Cadastrado em</div>
                            <div className="text-white font-medium">{formatDate(u.created_at)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Assinatura expira</div>
                            <div className={`font-medium ${u.subscription_expired ? 'text-red-500' : 'text-white'}`}>
                              {u.subscription_expires ? formatDate(u.subscription_expires) : '-'}
                              {u.subscription_expired && ' (Expirado)'}
                            </div>
                          </div>
                        </div>

                        {/* Plan Actions */}
                        <div className="mt-4 pt-4 border-t border-white/5">
                          <div className="text-xs text-gray-500 mb-2">Alterar plano:</div>
                          <div className="flex gap-2 flex-wrap">
                            {['free', 'basic', 'premium'].map((plan) => (
                              <Button
                                key={plan}
                                size="sm"
                                variant={u.subscription_type === plan ? 'default' : 'outline'}
                                disabled={u.subscription_type === plan}
                                onClick={() => updateUserPlan(u.user_id, plan)}
                                className={u.subscription_type === plan 
                                  ? 'bg-[#00FF94] text-black' 
                                  : 'border-white/10 text-gray-400 hover:text-white hover:border-white/20'
                                }
                              >
                                {getPlanLabel(plan)}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-white/5">
                <div className="text-3xl font-bold text-white">{suggestions.length}</div>
                <div className="text-gray-400 text-sm">Total</div>
              </div>
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-white/5">
                <div className="text-3xl font-bold text-yellow-500">
                  {suggestions.filter(s => s.status === 'pending').length}
                </div>
                <div className="text-gray-400 text-sm">Pendentes</div>
              </div>
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-white/5">
                <div className="text-3xl font-bold text-blue-500">
                  {suggestions.filter(s => s.status === 'reviewed').length}
                </div>
                <div className="text-gray-400 text-sm">Revisados</div>
              </div>
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-white/5">
                <div className="text-3xl font-bold text-green-500">
                  {suggestions.filter(s => s.status === 'implemented').length}
                </div>
                <div className="text-gray-400 text-sm">Implementados</div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-5 h-5 text-gray-400" />
              {['all', 'pending', 'reviewed', 'implemented'].map((status) => (
                <Button
                  key={status}
                  variant={filter === status ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setFilter(status);
                    setTimeout(fetchSuggestions, 100);
                  }}
                  className={filter === status 
                    ? 'bg-[#00FF94] text-black' 
                    : 'text-gray-400 hover:text-white'
                  }
                >
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </Button>
              ))}
            </div>

            {/* Suggestions List */}
            <div className="space-y-4">
              {suggestions.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Nenhuma sugestão encontrada</h3>
                </div>
              ) : (
                suggestions.map((suggestion, index) => (
                  <motion.div
                    key={suggestion.suggestion_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${getStatusColor(suggestion.status)} mb-3`}>
                          {suggestion.status === 'pending' && <Clock className="w-4 h-4" />}
                          {suggestion.status === 'reviewed' && <Lightbulb className="w-4 h-4" />}
                          {suggestion.status === 'implemented' && <CheckCircle className="w-4 h-4" />}
                          {getStatusLabel(suggestion.status)}
                        </div>

                        <p className="text-white text-lg mb-4 whitespace-pre-wrap">
                          {suggestion.suggestion_text}
                        </p>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {suggestion.user_name}
                          </div>
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {suggestion.user_email}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(suggestion.created_at)}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap lg:flex-col gap-2">
                        {suggestion.status !== 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateSuggestionStatus(suggestion.suggestion_id, 'pending')}
                            className="text-yellow-500 hover:bg-yellow-500/10"
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            Pendente
                          </Button>
                        )}
                        {suggestion.status !== 'reviewed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateSuggestionStatus(suggestion.suggestion_id, 'reviewed')}
                            className="text-blue-500 hover:bg-blue-500/10"
                          >
                            <Lightbulb className="w-4 h-4 mr-1" />
                            Revisar
                          </Button>
                        )}
                        {suggestion.status !== 'implemented' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateSuggestionStatus(suggestion.suggestion_id, 'implemented')}
                            className="text-green-500 hover:bg-green-500/10"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Implementado
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteSuggestion(suggestion.suggestion_id)}
                          className="text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Deletar
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Security Stats */}
            {securityStats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Shield className="w-8 h-8 text-red-500" />
                  </div>
                  <div className="text-3xl font-bold text-white">{securityStats.blocked_ips_count}</div>
                  <div className="text-gray-400 text-sm">IPs Bloqueados</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-[#00FF94]" />
                  </div>
                  <div className="text-3xl font-bold text-white">{securityStats.active_sessions}</div>
                  <div className="text-gray-400 text-sm">Sessões Ativas</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-white">{securityStats.last_24h?.logins || 0}</div>
                  <div className="text-gray-400 text-sm">Logins (24h)</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <TrendingDown className="w-8 h-8 text-yellow-500" />
                  </div>
                  <div className="text-3xl font-bold text-white">{securityStats.last_24h?.failed_logins || 0}</div>
                  <div className="text-gray-400 text-sm">Falhas de Login (24h)</div>
                </motion.div>
              </div>
            )}

            {/* Rate Limit Config */}
            {securityStats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Configurações de Segurança</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-[#141414] rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Máx. Tentativas Falhas</div>
                    <div className="text-2xl font-bold text-white">{securityStats.rate_limit_config?.max_failed_attempts || 5}</div>
                  </div>
                  <div className="bg-[#141414] rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Tempo de Bloqueio</div>
                    <div className="text-2xl font-bold text-white">{securityStats.rate_limit_config?.block_duration_minutes || 15} min</div>
                  </div>
                  <div className="bg-[#141414] rounded-lg p-4">
                    <div className="text-gray-400 text-sm mb-1">Timeout de Sessão</div>
                    <div className="text-2xl font-bold text-white">{securityStats.rate_limit_config?.session_timeout_minutes || 30} min</div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Block IP Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Bloquear IP</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Endereço IP (ex: 192.168.1.1)"
                  value={newBlockIp}
                  onChange={(e) => setNewBlockIp(e.target.value)}
                  className="flex-1 px-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]/50"
                />
                <input
                  type="text"
                  placeholder="Motivo (opcional)"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  className="flex-1 px-4 py-2 bg-[#141414] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00FF94]/50"
                />
                <Button
                  onClick={blockIp}
                  className="bg-red-500 text-white hover:bg-red-600"
                >
                  Bloquear
                </Button>
              </div>
            </motion.div>

            {/* Blocked IPs List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">IPs Bloqueados ({blockedIps.length})</h3>
              {blockedIps.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nenhum IP bloqueado</p>
              ) : (
                <div className="space-y-2">
                  {blockedIps.map((ip, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#141414] rounded-lg">
                      <div>
                        <div className="text-white font-mono">{ip.ip_address}</div>
                        <div className="text-xs text-gray-400">
                          {ip.reason} • Bloqueado por {ip.blocked_by} em {formatDate(ip.blocked_at)}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unblockIp(ip.ip_address)}
                        className="text-green-500 hover:bg-green-500/10"
                      >
                        Desbloquear
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Recent Alerts */}
            {securityStats?.recent_alerts && securityStats.recent_alerts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-[#0A0A0A] rounded-xl p-6 border border-red-500/20"
              >
                <h3 className="text-lg font-semibold text-red-500 mb-4">Alertas Recentes (Alta Severidade)</h3>
                <div className="space-y-2">
                  {securityStats.recent_alerts.map((alert, index) => (
                    <div key={index} className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              alert.severity === 'critical' ? 'bg-red-500 text-white' : 'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {alert.severity}
                            </span>
                            <span className="text-white font-medium">{alert.action}</span>
                          </div>
                          <div className="text-sm text-gray-400 mt-1">{alert.details}</div>
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(alert.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Audit Logs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="bg-[#0A0A0A] rounded-xl p-6 border border-white/5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Logs de Auditoria</h3>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  {['all', 'info', 'warning', 'high'].map((sev) => (
                    <Button
                      key={sev}
                      variant={auditFilter === sev ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        setAuditFilter(sev);
                        setTimeout(fetchAuditLogs, 100);
                      }}
                      className={auditFilter === sev ? 'bg-[#00FF94] text-black' : 'text-gray-400 hover:text-white'}
                    >
                      {sev === 'all' ? 'Todos' : sev}
                    </Button>
                  ))}
                </div>
              </div>
              
              {auditLogs.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nenhum log encontrado</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 text-gray-400 font-medium">Data</th>
                        <th className="text-left py-2 text-gray-400 font-medium">Ação</th>
                        <th className="text-left py-2 text-gray-400 font-medium">Detalhes</th>
                        <th className="text-left py-2 text-gray-400 font-medium">Usuário</th>
                        <th className="text-left py-2 text-gray-400 font-medium">IP</th>
                        <th className="text-left py-2 text-gray-400 font-medium">Severidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log, index) => (
                        <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 text-gray-400 text-xs">{formatDate(log.created_at)}</td>
                          <td className="py-2 text-white">{log.action}</td>
                          <td className="py-2 text-gray-300 max-w-xs truncate">{log.details}</td>
                          <td className="py-2 text-gray-400">{log.user_email || '-'}</td>
                          <td className="py-2 text-gray-400 font-mono text-xs">{log.ip_address || '-'}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              log.severity === 'critical' ? 'bg-red-500 text-white' :
                              log.severity === 'high' ? 'bg-yellow-500/20 text-yellow-500' :
                              log.severity === 'warning' ? 'bg-orange-500/20 text-orange-500' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {log.severity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSuggestions;
